import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { ChangeEvent, DragEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FiEdit2, FiFile, FiPlusCircle, FiTrash2, FiUpload } from 'react-icons/fi';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import InputSelectMobile from '../../../components/Input/Mobile/Select';
import MainCard from '../../../components/MainCard/Index';
import CardHeaderMobile from '../../../components/Mobile/header';
import NavigationButtonMobilePage from '../../../components/Mobile/navigationButton';
import TitleSectionMobilePage from '../../../components/Mobile/titleSection';
import { ContextAuth } from '../../../contexts/Authentication';
import {
  IRegistrationDocument,
  useDeleteRegistrationDocument,
  useRegistrationDocumentStatus,
  useUploadRegistrationDocument,
} from '../../../services/hooks/Registration/useVacancyDocuments';
import { formatFileSize } from '../../../utils/functions';
import { IDataWheelPickerSelect } from '../../../components/Input/Mobile/Select/whellPicker';

const ACCEPTED_TYPES = ['application/pdf'];
const ACCEPTED_FILE_EXTENSIONS = ['pdf'];

const getErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) return 'Não foi possível concluir a operação. Tente novamente.';
  const data = error.response?.data as { detail?: string; message?: string } | undefined;
  return data?.detail || data?.message || 'Não foi possível concluir a operação. Tente novamente.';
};

const truncateFileName = (name: string, maxLength = 50) => {
  if (name.length <= maxLength) return name;

  const visibleLength = maxLength - 3;
  const startLength = Math.ceil(visibleLength / 2);
  const endLength = Math.floor(visibleLength / 2);
  return `${name.slice(0, startLength)}...${name.slice(-endLength)}`;
};

const VacancyDocumentsPage: React.FC = () => {
  const { isMobile } = useContext(ContextAuth);
  const { selectiveProcessId, registrationId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const uploadDialog = useDisclosure();
  const replaceDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const statusQuery = useRegistrationDocumentStatus(registrationId);
  const uploadMutation = useUploadRegistrationDocument(registrationId);
  const deleteMutation = useDeleteRegistrationDocument(registrationId);
  const documents = statusQuery.data?.documents ?? [];
  const uploadedDocuments = useMemo(
    () => documents.filter((document) => document.uploaded && document.attachment),
    [documents],
  );
  const selectedDocument = useMemo(
    () => documents.find((document) => document.type === selectedType),
    [documents, selectedType],
  );
  const isMutating = uploadMutation.isPending || deleteMutation.isPending;
  const isCandidateAreaAccess = searchParams.get('from') === 'candidate';

  useEffect(() => {
    if (!documents.length || documents.some((document) => document.type === selectedType)) return;
    setSelectedType((documents.find((document) => !document.uploaded) ?? documents[0]).type);
  }, [documents, selectedType]);

  if (!selectiveProcessId || !registrationId) return <Navigate to="/painel/inscricoes" replace />;

  const showError = (error: unknown) =>
    toast({
      title: 'Não foi possível alterar o documento',
      description: getErrorMessage(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  const uploadFile = async (file: File, document: IRegistrationDocument) => {
    let uploadStartedAt: number | undefined;
    try {
      const previousAttachmentId = document.attachment?.id;
      uploadStartedAt = Date.now();
      setIsUploadingFile(true);
      setUploadProgress(0);
      await uploadMutation.mutateAsync({ type: document.type, file, onProgress: setUploadProgress });
      setUploadProgress(100);
      if (previousAttachmentId) await deleteMutation.mutateAsync(previousAttachmentId);
      toast({ title: 'Documento enviado com sucesso.', status: 'success', duration: 3500, isClosable: true });
    } catch (error) {
      showError(error);
    } finally {
      if (uploadStartedAt) {
        const remainingAnimationTime = Math.max(0, 500 - (Date.now() - uploadStartedAt));
        if (remainingAnimationTime) await new Promise((resolve) => setTimeout(resolve, remainingAnimationTime));
      }
      setIsUploadingFile(false);
      setPendingFile(null);
      setUploadProgress(0);
      uploadDialog.onClose();
      replaceDialog.onClose();
    }
  };

  const validateAndPrepareFile = (file?: File) => {
    if (!file || !selectedDocument) return;
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPTED_TYPES.includes(file.type) && !ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
      toast({
        title: 'Formato inválido',
        description: 'Envie um arquivo PDF, JPG, JPEG ou PNG.',
        status: 'warning',
        duration: 4500,
        isClosable: true,
      });
      return;
    }
    if (file.size === 0) {
      toast({
        title: 'Arquivo vazio',
        description: 'Selecione um arquivo que possua conteúdo.',
        status: 'warning',
        duration: 4500,
        isClosable: true,
      });
      return;
    }
    if (!statusQuery.data?.can_upload) {
      toast({ title: 'Esta inscrição não permite alteração de documentos.', status: 'warning', isClosable: true });
      return;
    }
    setPendingFile(file);
    setUploadProgress(0);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    validateAndPrepareFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 1) {
      toast({ title: 'Envie somente um arquivo por tipo.', status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    validateAndPrepareFile(event.dataTransfer.files?.[0]);
  };

  const openUploadDialog = (type?: string) => {
    const defaultDocument = documents.find((document) => !document.uploaded) ?? documents[0];
    setIsEditingDocument(Boolean(type));
    setSelectedType(type || defaultDocument?.type || '');
    setPendingFile(null);
    setUploadProgress(0);
    uploadDialog.onOpen();
  };

  const handleUpload = () => {
    if (!pendingFile || !selectedDocument) {
      toast({ title: 'Selecione o tipo e o arquivo que deseja enviar.', status: 'warning', isClosable: true });
      return;
    }
    if (selectedDocument.attachment && !isEditingDocument) replaceDialog.onOpen();
    else void uploadFile(pendingFile, selectedDocument);
  };

  const openDeleteDialog = (type: string) => {
    setSelectedType(type);
    deleteDialog.onOpen();
  };

  const handleDelete = async () => {
    if (!selectedDocument?.attachment) return;
    try {
      await deleteMutation.mutateAsync(selectedDocument.attachment.id);
      toast({ title: 'Documento removido.', status: 'info', duration: 3000, isClosable: true });
      deleteDialog.onClose();
    } catch (error) {
      showError(error);
    }
  };

  const handleContinue = () => {
    if (!statusQuery.data?.all_uploaded) {
      toast({
        title: 'Envie todos os documentos obrigatórios antes de continuar.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    const standby = searchParams.get('standbyExemption') === 'true' ? '&standbyExemption=true' : '';
    navigate(
      `/processo-seletivo/${selectiveProcessId}/inscricao/${registrationId}/resumo?previous-route=/processo-seletivo/inscricao${standby}`,
    );
  };

  const handleBack = () => {
    if (isCandidateAreaAccess) navigate('/painel/inscricoes');
    else navigate(-1);
  };

  return (
    <>
      {isMobile && (
        <Stack height="100%" w="100%" bg="white" gap={0}>
          <Header />
          <VStack flex={1} overflowY="auto" py={2} pb="84px" align="stretch">
            <Box
              mx={3}
              p={5}
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.100"
              boxShadow="lg"
              bg="white"
              flex={1}
            >
              <Stack spacing={5}>
                <CardHeaderMobile
                  title="DOCUMENTAÇÃO OBRIGATÓRIA EXIGIDA"
                  subTitle="Selecione o tipo e envie um arquivo para cada documento exigido."
                  colorScheme="blue"
                />

                {statusQuery.isLoading && (
                  <VStack justify="center" py={12}>
                    <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
                    <Text fontSize="sm">Buscando documentos da inscrição...</Text>
                  </VStack>
                )}

                {statusQuery.isError && (
                  <Alert status="error" borderRadius="md" flexDirection="column" textAlign="center" gap={3}>
                    <AlertIcon mr={0} />
                    <Text fontSize="sm">Não foi possível carregar os documentos desta inscrição.</Text>
                    <Button size="sm" colorScheme="red" variant="outline" onClick={() => void statusQuery.refetch()}>
                      Tentar novamente
                    </Button>
                  </Alert>
                )}

                {statusQuery.data && (
                  <Stack spacing={4}>
                    <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                      <TitleSectionMobilePage mb={0}>Documentos enviados</TitleSectionMobilePage>
                    </Flex>

                    {uploadedDocuments.length === 0 && documents.length > 0 && (
                      <VStack
                        justify="center"
                        py={10}
                        px={4}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        textAlign="center"
                      >
                        <Icon as={FiFile} boxSize={10} color="gray.400" />
                        <Text fontSize="sm" color="gray.500">
                          Quando você adicionar um arquivo, ele aparecerá aqui.
                        </Text>
                      </VStack>
                    )}

                    {uploadedDocuments.map((document) => (
                      <Stack
                        key={document.type}
                        spacing={3}
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="sm"
                        bg="white"
                        overflow="hidden"
                      >
                        <Flex justify="space-between" align="flex-start" gap={3}>
                          <Text fontSize="sm" fontWeight="bold">
                            {document.label}
                          </Text>
                          <Badge colorScheme="green" borderRadius="full" px={2.5} py={1} fontSize="10px">
                            Enviado
                          </Badge>
                        </Flex>
                        {document.attachment && (
                          <Stack spacing={0} minW={0}>
                            <Link
                              href={document.attachment.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="blue.600"
                              fontSize="sm"
                              fontWeight="medium"
                              textDecoration="underline"
                              isTruncated
                              title={document.attachment.name}
                            >
                              {document.attachment.name}
                            </Link>
                            <Text fontSize="xs" color="gray.500">
                              {formatFileSize(document.attachment.size)} ·{' '}
                              {new Date(document.attachment.created_at).toLocaleString('pt-BR')}
                            </Text>
                          </Stack>
                        )}
                        {statusQuery.data.can_upload && document.attachment && (
                          <HStack justify="flex-end" spacing={2} pt={1}>
                            <Button
                              leftIcon={<FiEdit2 size={16} />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => openUploadDialog(document.type)}
                            >
                              Alterar
                            </Button>
                            <Button
                              leftIcon={<FiTrash2 size={16} />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => openDeleteDialog(document.type)}
                            >
                              Excluir
                            </Button>
                          </HStack>
                        )}
                      </Stack>
                    ))}

                    {statusQuery.data.can_upload && documents.length > 0 && (
                      <Button
                        leftIcon={<FiPlusCircle size={22} />}
                        size="md"
                        colorScheme="blue"
                        variant="outline"
                        borderRadius="full"
                        alignSelf="center"
                        mt={2}
                        onClick={() => openUploadDialog()}
                      >
                        Enviar documento
                      </Button>
                    )}

                    {documents.length === 0 && (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">Não há tipos de documentos configurados para esta inscrição.</Text>
                      </Alert>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          </VStack>
          {statusQuery.data && (
            <NavigationButtonMobilePage
              absolute
              mainButtonText="Concluir"
              onGoMainButton={isCandidateAreaAccess ? () => navigate('/painel/inscricoes') : handleContinue}
              mainButtonDisabled={(!isCandidateAreaAccess && !statusQuery.data.all_uploaded) || isMutating}
              isLoading={isMutating}
              goBackButtonText={isCandidateAreaAccess ? 'Voltar' : undefined}
              onGoBackButton={isCandidateAreaAccess ? handleBack : undefined}
              w={isCandidateAreaAccess ? '42%' : undefined}
            />
          )}
        </Stack>
      )}

      {!isMobile && (
        <MainCard
          title="PROCESSO SELETIVO - "
          subtitle="DOCUMENTAÇÃO OBRIGATÓRIA EXIGIDA"
          description="Selecione o tipo e envie um arquivo para cada documento exigido."
        >
          <Stack px={[5, 5, 8, 12]} pb={10} spacing={5}>
            {statusQuery.isLoading && (
              <Flex justify="center" py={10}>
                <Spinner size="xl" color="blue.500" />
              </Flex>
            )}
            {statusQuery.isError && (
              <Alert status="error" borderRadius="md" justifyContent="space-between" gap={3} flexWrap="wrap">
                <HStack>
                  <AlertIcon />
                  <Text>Não foi possível carregar os documentos desta inscrição.</Text>
                </HStack>
                <Button size="sm" colorScheme="red" variant="outline" onClick={() => void statusQuery.refetch()}>
                  Tentar novamente
                </Button>
              </Alert>
            )}
            {statusQuery.data && (
              <>
                {/* {!statusQuery.data.can_upload && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />A inscrição está finalizada. Os documentos estão disponíveis somente para consulta.
              </Alert>
            )} */}
                {documents.length > 0 && (
                  <Stack border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" spacing={0}>
                    <Flex justify="space-between" align="center" gap={3} px={4} py={3} bg="gray.50" wrap="wrap">
                      <HStack spacing={2}>
                        <Icon as={FiFile} boxSize={5} color="blue.600" />
                        <Text fontSize="lg" fontWeight="bold">
                          DOCUMENTAÇÃO OBRIGATÓRIA EXIGIDA
                        </Text>
                      </HStack>
                      {statusQuery.data.can_upload && (
                        <Button
                          variant="ghost"
                          color="blue.600"
                          bg="blue.50"
                          border="1px solid"
                          borderColor="blue.200"
                          borderRadius="full"
                          fontWeight="bold"
                          height="auto"
                          pl={3.5}
                          pr={6}
                          py={1.5}
                          _hover={{ bg: 'blue.600', borderColor: 'blue.600', color: 'blue.50' }}
                          onClick={() => openUploadDialog()}
                        >
                          <HStack spacing={2} width="100%" justify="center" align="center" color="inherit">
                            <Icon as={FiPlusCircle} boxSize={7} />
                            <Text fontSize="sm" color="inherit" lineHeight="normal" fontWeight="bold">
                              Enviar nova documentação
                            </Text>
                          </HStack>
                        </Button>
                      )}
                    </Flex>
                    {uploadedDocuments.length > 0 && (
                      <Grid
                        display={{ base: 'none', md: 'grid' }}
                        templateColumns="minmax(0, 35fr) minmax(0, 35fr) minmax(150px, 30fr) 190px"
                        gap={5}
                        px={4}
                        py={2.5}
                        borderTop="1px solid"
                        borderColor="gray.200"
                        bg="gray.100"
                        alignItems="center"
                      >
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">
                          TIPO DE DOCUMENTO
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">
                          ARQUIVO ENVIADO
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center">
                          SITUAÇÃO
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center">
                          AÇÕES
                        </Text>
                      </Grid>
                    )}
                    {uploadedDocuments.map((document) => (
                      <Grid
                        key={document.type}
                        templateColumns={{
                          base: 'minmax(0, 1fr) auto',
                          md: 'minmax(0, 35fr) minmax(0, 35fr) minmax(150px, 30fr) 190px',
                        }}
                        alignItems="center"
                        gap={{ base: 3, md: 5 }}
                        px={4}
                        py={4}
                        borderTop="1px solid"
                        borderColor="gray.200"
                        _hover={{ bg: 'gray.50' }}
                        transition="background 0.15s ease"
                      >
                        <GridItem colSpan={{ base: 2, md: 1 }} order={{ base: 1, md: 0 }} minW={0}>
                          <Text fontSize="sm" fontWeight="semibold">
                            {document.label}
                          </Text>
                        </GridItem>
                        <GridItem colSpan={{ base: 2, md: 1 }} order={{ base: 2, md: 0 }} minW={0} overflow="hidden">
                          {document.attachment ? (
                            <Stack spacing={0} minW={0}>
                              <Link
                                href={document.attachment.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontSize="sm"
                                color="blue.600"
                                fontWeight="medium"
                                textDecoration="underline"
                                display="block"
                                width="100%"
                                isTruncated
                                maxW="100%"
                                title={document.attachment.name}
                              >
                                {truncateFileName(document.attachment.name)}
                              </Link>
                              <Text fontSize="xs" color="gray.500">
                                {formatFileSize(document.attachment.size)} ·{' '}
                                {new Date(document.attachment.created_at).toLocaleString('pt-BR')}
                              </Text>
                            </Stack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">
                              Nenhum arquivo enviado
                            </Text>
                          )}
                        </GridItem>
                        <GridItem order={{ base: 3, md: 0 }} justifySelf="center">
                          <Badge colorScheme="green" borderRadius="full" px={2.5} py={1} fontSize="xs">
                            Enviado
                          </Badge>
                        </GridItem>
                        {statusQuery.data.can_upload && (
                          <GridItem order={{ base: 4, md: 0 }} justifySelf="center">
                            {document.attachment && (
                              <HStack spacing={1}>
                                <Button
                                  aria-label="Alterar arquivo"
                                  leftIcon={<FiEdit2 size={18} />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => openUploadDialog(document.type)}
                                >
                                  Alterar
                                </Button>
                                <Button
                                  aria-label="Remover arquivo"
                                  leftIcon={<FiTrash2 size={18} />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => openDeleteDialog(document.type)}
                                >
                                  Excluir
                                </Button>
                              </HStack>
                            )}
                          </GridItem>
                        )}
                      </Grid>
                    ))}
                    {uploadedDocuments.length === 0 && (
                      <Stack
                        align="center"
                        justify="center"
                        spacing={3}
                        px={6}
                        py={12}
                        borderTop="1px solid"
                        borderColor="gray.200"
                        textAlign="center"
                      >
                        <Icon as={FiFile} boxSize={12} color="gray.400" />
                        <Text color="gray.500" maxW="420px">
                          Quando você adicionar um arquivo, ele aparecerá aqui.
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                )}
                {documents.length === 0 && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    Não há tipos de documentos configurados para esta inscrição.
                  </Alert>
                )}
                <Flex justify="flex-end" gap={3} wrap="wrap">
                  {isCandidateAreaAccess && (
                    <Button variant="outline" minW="150px" onClick={handleBack}>
                      Voltar
                    </Button>
                  )}
                  <Button
                    bg="primaryColor"
                    color="white"
                    _hover={{ opacity: 0.9 }}
                    minW="150px"
                    onClick={isCandidateAreaAccess ? () => navigate('/painel/inscricoes') : handleContinue}
                    isDisabled={(!isCandidateAreaAccess && !statusQuery.data.all_uploaded) || isMutating}
                  >
                    Concluir
                  </Button>
                </Flex>
              </>
            )}
          </Stack>
        </MainCard>
      )}

      <Modal isOpen={uploadDialog.isOpen} onClose={uploadDialog.onClose} isCentered={!isMobile} size="xl">
        <ModalOverlay />
        <ModalContent
          position={isMobile ? 'absolute' : undefined}
          bottom={isMobile ? 0 : undefined}
          w={isMobile ? '100%' : undefined}
          maxW={isMobile ? '100dvw' : undefined}
          maxH={isMobile ? '90dvh' : undefined}
          m={isMobile ? 0 : undefined}
          borderRadius={isMobile ? '18px 18px 0 0' : undefined}
          overflowY={isMobile ? 'auto' : undefined}
        >
          <Box
            position={isMobile ? 'sticky' : 'relative'}
            top={0}
            zIndex={2}
            bg="white"
            borderRadius={isMobile ? '18px 18px 0 0' : undefined}
          >
            <ModalHeader>{isEditingDocument ? 'Alterar documento' : 'Adicionar documento'}</ModalHeader>
            <ModalCloseButton />
          </Box>
          <ModalBody>
            <Stack spacing={4}>
              {isMobile ? (
                <InputSelectMobile
                  name="document_type"
                  placeholderLabel="Tipo de documento"
                  isRequired
                  isExternalState
                  listItens={documents.map((document) => ({ id: document.type, name: document.label }))}
                  selectedItem={
                    selectedDocument ? { id: selectedDocument.type, name: selectedDocument.label } : undefined
                  }
                  onChangeItem={(item: IDataWheelPickerSelect | undefined) => {
                    setSelectedType(item?.id ?? '');
                    setPendingFile(null);
                  }}
                  isDisabled={documents.length === 0 || isMutating || isEditingDocument}
                />
              ) : (
                <FormControl isRequired>
                  <FormLabel>Tipo de documento</FormLabel>
                  <Select
                    value={selectedType}
                    onChange={(event) => {
                      setSelectedType(event.target.value);
                      setPendingFile(null);
                    }}
                    isDisabled={documents.length === 0 || isMutating || isEditingDocument}
                  >
                    <option value="">Selecione...</option>
                    {documents.map((document) => (
                      <option key={document.type} value={document.type}>
                        {document.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Input ref={inputRef} type="file" accept="application/pdf" hidden onChange={handleFileChange} />
              <Box
                border="2px dashed"
                borderColor={isDragging ? 'blue.400' : 'gray.300'}
                bg={isDragging ? 'blue.50' : 'white'}
                borderRadius="lg"
                p={8}
                textAlign="center"
                cursor={!selectedType || isMutating ? 'not-allowed' : 'pointer'}
                opacity={!selectedType ? 0.6 : 1}
                onClick={() => selectedType && !isMutating && inputRef.current?.click()}
                onDragEnter={(event) => {
                  event.preventDefault();
                  if (selectedType) setIsDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <Stack align="center">
                  <Icon as={FiUpload} boxSize={8} color="blue.500" />
                  <Text fontWeight="semibold">Arraste o arquivo ou clique para selecionar</Text>
                  <Text fontSize="sm" color="gray.500">
                    Formatos aceitos: PDF. Um arquivo por tipo.
                  </Text>
                </Stack>
              </Box>

              {selectedDocument?.attachment && (
                <Stack spacing={1.5}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Arquivo enviado
                  </Text>
                  <HStack p={3} border="1px solid" borderColor="blue.200" bg="blue.50" borderRadius="md">
                    <Icon as={FiFile} color="blue.600" boxSize={5} />
                    <Stack spacing={0} minW={0} flex={1}>
                      <Link
                        href={selectedDocument.attachment.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.600"
                        fontSize="sm"
                        fontWeight="semibold"
                        textDecoration="underline"
                        isTruncated
                        title={selectedDocument.attachment.name}
                      >
                        {selectedDocument.attachment.name}
                      </Link>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(selectedDocument.attachment.size)} ·{' '}
                        {new Date(selectedDocument.attachment.created_at).toLocaleString('pt-BR')}
                      </Text>
                    </Stack>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Ao enviar um novo arquivo, o atual será substituído.
                  </Text>
                </Stack>
              )}

              {pendingFile && (
                <Box border="1px solid" borderColor="green.200" bg="green.50" borderRadius="md" overflow="hidden">
                  <HStack p={3}>
                    <Icon as={FiFile} color="green.600" />
                    <Stack spacing={0} minW={0} flex={1}>
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                        {pendingFile.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(pendingFile.size)}
                      </Text>
                    </Stack>
                    <IconButton
                      aria-label="Remover arquivo selecionado"
                      icon={<FiTrash2 size={20} />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      isDisabled={isUploadingFile}
                      onClick={() => {
                        setPendingFile(null);
                        setUploadProgress(0);
                      }}
                    />
                  </HStack>
                  {isUploadingFile && (
                    <Progress
                      value={uploadProgress}
                      width="100%"
                      height="5px"
                      colorScheme="green"
                      borderRadius={0}
                      m={0}
                    />
                  )}
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter
            gap={3}
            position={isMobile ? 'sticky' : undefined}
            bottom={isMobile ? 0 : undefined}
            zIndex={isMobile ? 2 : undefined}
            bg="white"
            borderTop={isMobile ? '1px solid' : undefined}
            borderColor={isMobile ? 'gray.100' : undefined}
          >
            <Button variant="ghost" onClick={uploadDialog.onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiUpload />}
              isLoading={isMutating}
              isDisabled={!selectedType || !pendingFile}
              onClick={handleUpload}
            >
              {isEditingDocument ? 'Alterar arquivo' : 'Enviar arquivo'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={replaceDialog.isOpen} onClose={replaceDialog.onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={isMobile ? 4 : undefined} maxW={isMobile ? 'calc(100vw - 32px)' : undefined}>
          <Box position="relative">
            <ModalHeader>Documento já enviado</ModalHeader>
            <ModalCloseButton />
          </Box>
          <ModalBody>
            Você já enviou a documentação para esse tipo de documento. Caso deseje modificar este envio, clique em
            Alterar.
          </ModalBody>
          <ModalFooter gap={3}>
            <Button colorScheme="blue" onClick={replaceDialog.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={isMobile ? 4 : undefined} maxW={isMobile ? 'calc(100vw - 32px)' : undefined}>
          <ModalHeader>Remover documento</ModalHeader>
          <ModalCloseButton />
          <ModalBody>Tem certeza que deseja remover o arquivo enviado para este tipo?</ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={deleteDialog.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="red" isLoading={deleteMutation.isPending} onClick={() => void handleDelete()}>
              Remover
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default VacancyDocumentsPage;
